import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, fmtDate, fmtDateTime, AGING, PRIORITY } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import NumberInput from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import LeadFormDialog from "@/components/LeadFormDialog";
import { toast } from "sonner";
import { Loader2, Phone, MapPin, MessageCircle, Pencil, Trash2, Lock, Unlock, FilePlus, MessageSquareText, History, RotateCcw, FileText, Printer, Sparkles, Wallet, Mail, Calculator, Copy } from "lucide-react";
import DocumentDialog from "@/components/DocumentDialog";
import KalkulatorBuilder from "@/components/KalkulatorBuilder";
import ChatBox from "@/components/ChatBox";
import PaymentTermins from "@/components/PaymentTermins";
import AssignSalesDropdown from "@/components/AssignSalesDropdown";
import ScheduledActivityBoard from "@/components/ScheduledActivityBoard";
import DateTimePicker from "@/components/ui/date-time-picker";

const AI_SCORE_CLS = "bg-blue-500/15 text-blue-300 border-blue-500/30";
// AI Sales Copilot is temporarily locked (Coming Soon) — backend/logic kept intact, just gated in the UI.
const AI_COPILOT_ENABLED = false;

export default function LeadDetailSheet({ leadId, initialTab, onClose }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [note, setNote] = useState("");
  const [fu, setFu] = useState("");
  const [addNote, setAddNote] = useState("");
  const [addAmount, setAddAmount] = useState("");
  const [estEditing, setEstEditing] = useState(false);
  const [estNilai, setEstNilai] = useState("");
  const [estNote, setEstNote] = useState("");
  const [designEditing, setDesignEditing] = useState(false);
  const [designNilai, setDesignNilai] = useState("");
  const [prodEditing, setProdEditing] = useState(false);
  const [prodNilai, setProdNilai] = useState("");
  const [prodNote, setProdNote] = useState("");
  const [copilot, setCopilot] = useState(null);
  const [copLoading, setCopLoading] = useState(false);
  const [copErr, setCopErr] = useState("");
  const readOnly = false;
  const canUnlock = user?.role !== "sales"; // sales can lock a closed deal, but only admin/owner can re-open it

  // Draft edit state (open dialogs, half-typed values) must not leak from one lead into
  // the next when switching leadId without fully unmounting this sheet (e.g. clicking
  // through the Leads list) — reset it every time the selected lead changes.
  useEffect(() => {
    setEditOpen(false); setNote(""); setFu(""); setAddNote(""); setAddAmount("");
    setEstEditing(false); setEstNilai(""); setEstNote("");
    setDesignEditing(false); setDesignNilai("");
    setProdEditing(false); setProdNilai(""); setProdNote("");
    setCopilot(null); setCopLoading(false); setCopErr("");
  }, [leadId]);

  const { data: lead, isLoading, isError } = useQuery({
    queryKey: ["lead", leadId], enabled: !!leadId,
    queryFn: async () => (await api.get(`/leads/${leadId}`)).data,
    retry: false,
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["lead", leadId] }); qc.invalidateQueries({ queryKey: ["leads"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };

  const addActivity = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/leads/${leadId}/activity`, { note, follow_up_date: fu ? new Date(fu).toISOString() : null });
      setNote(""); setFu(""); toast.success("Aktivitas dicatat"); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async () => {
    if (!window.confirm("Hapus lead ini?")) return;
    try { await api.delete(`/leads/${leadId}`); toast.success("Lead dihapus"); refresh(); onClose(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const toggleLock = async () => {
    try {
      await api.patch(`/leads/${leadId}/lock`, { locked: !lead.locked });
      toast.success(!lead.locked ? "Data dikunci 🔒" : "Kunci dibuka 🔓"); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const addAddendum = async () => {
    if (!addNote.trim()) { toast.error("Isi keterangan adendum"); return; }
    try {
      await api.post(`/leads/${leadId}/addendum`, { note: addNote, amount: Number(addAmount) || 0 });
      setAddNote(""); setAddAmount(""); toast.success("Adendum ditambahkan"); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveEstimasi = async () => {
    const nilai = Number(estNilai);
    if (!Number.isFinite(nilai) || nilai < 0) { toast.error("Isi estimasi anggaran (angka ≥ 0)"); return; }
    try {
      await api.put(`/leads/${leadId}/estimasi`, { estimasi_anggaran: nilai, note: estNote });
      toast.success("Estimasi anggaran disimpan"); setEstEditing(false); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveDesign = async () => {
    const nilai = Number(designNilai);
    if (!Number.isFinite(nilai) || nilai < 0) { toast.error("Isi nilai design (angka ≥ 0)"); return; }
    try {
      await api.put(`/leads/${leadId}`, { nama: lead.nama, nilai_deal: nilai });
      toast.success("Design disimpan"); setDesignEditing(false); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveProduksi = async () => {
    const nilai = Number(prodNilai);
    if (!Number.isFinite(nilai) || nilai < 0) { toast.error("Isi nilai produksi (angka ≥ 0)"); return; }
    try {
      await api.put(`/leads/${leadId}/produksi`, { nilai_produksi: nilai, note: prodNote });
      toast.success("Produksi disimpan"); setProdEditing(false); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const restore = async () => {
    try { await api.patch(`/leads/${leadId}/restore`); toast.success("Data dikembalikan"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const getCopilot = async () => {
    setCopLoading(true); setCopErr("");
    try { const { data } = await api.post(`/leads/${leadId}/copilot`); setCopilot(data); }
    catch (e) { setCopErr(apiErr(e)); toast.error(apiErr(e)); }
    finally { setCopLoading(false); }
  };

  const { data: templates = [] } = useQuery({ queryKey: ["wa-templates"], queryFn: async () => (await api.get("/wa-templates")).data });
  const { data: audit = [] } = useQuery({ queryKey: ["audit", leadId], enabled: !!leadId && !isError, retry: false, queryFn: async () => (await api.get(`/leads/${leadId}/audit`)).data });
  const { data: docs = [] } = useQuery({ queryKey: ["documents", leadId], enabled: !!leadId && !isError, retry: false, queryFn: async () => (await api.get(`/documents?lead_id=${leadId}`)).data });
  const [docOpen, setDocOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [kalkOpen, setKalkOpen] = useState(false);
  const [editingKalk, setEditingKalk] = useState(null);
  const delDoc = async (id) => { try { await api.delete(`/documents/${id}`); toast.success("Dokumen dihapus"); qc.invalidateQueries({ queryKey: ["documents", leadId] }); } catch (e) { toast.error(apiErr(e)); } };
  // Same purpose regardless of who needs it twice: a client wants two nearly-identical
  // quotes (different room count, say), or a repeat project. Copies every field except the
  // server-generated ones (doc_id, number, computed totals) — the backend recomputes a
  // fresh number and totals same as any other create, and goes through the same
  // Admin-needs-approval flow as a from-scratch document of that type.
  const duplicateDoc = async (d) => {
    const payload = { ...d, number: "", date: new Date().toISOString().slice(0, 10) };
    delete payload.doc_id; delete payload.created_at; delete payload.updated_at; delete payload.created_by;
    delete payload.total; delete payload.subtotal; delete payload.discount_amount; delete payload.tax;
    delete payload.payment_status_actual; delete payload.sisa_tagihan_actual;
    try {
      const { data } = await api.post("/documents", payload);
      toast.success(data?.pending ? "Duplikat diajukan, menunggu persetujuan" : "Dokumen diduplikasi");
      qc.invalidateQueries({ queryKey: ["documents", leadId] });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const docTypeLabel = (d) => d.type === "quotation" ? "Quotation/SPH" : d.type === "kontrak_design" ? "Kontrak Design" : d.type === "kontrak_produksi" ? "Kontrak Produksi" : d.type === "kalkulator_estimasi" ? "Kalkulator Estimasi" : (d.type === "dokumen_lain" || d.type?.startsWith("doctype_")) ? (d.title || "Dokumen") : "Invoice";
  const regularDocs = docs.filter((d) => d.type !== "kalkulator_estimasi");
  const kalkDocs = docs.filter((d) => d.type === "kalkulator_estimasi");
  // Documents now send the actual generated PDF, not a bare link — the backend renders it
  // server-side via headless Chromium (same content as the manual "Cetak / Simpan PDF"
  // button). WhatsApp has no way to auto-attach a file from a plain wa.me link (a real
  // WhatsApp limitation, not something to code around without a full Business API), so that
  // flow downloads the PDF to the device first, then opens WhatsApp with an intro text
  // asking the admin to attach the file that just downloaded — one manual tap either way.
  // Email has no such limitation: the PDF goes out as a real attachment, fully automatic.
  const [sendingDoc, setSendingDoc] = useState(null); // doc_id currently being sent, for the button's own loading state
  const sendDocWa = async (d) => {
    const raw = lead?.no_hp || d.client?.telp;
    const phone = raw ? raw.replace(/[^0-9]/g, "").replace(/^0/, "62") : null;
    if (!phone) return toast.error("Nomor WhatsApp client belum diisi — isi dulu di Edit Lead");
    setSendingDoc(d.doc_id);
    try {
      const [pdfRes, textRes] = await Promise.all([
        api.get(`/documents/${d.doc_id}/pdf`, { responseType: "blob" }),
        api.get(`/documents/${d.doc_id}/send-text`),
      ]);
      const filename = `${docTypeLabel(d)}_${d.number}.pdf`.replace(/[\\/]/g, "-");
      const url = URL.createObjectURL(pdfRes.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(textRes.data.text)}`, "_blank");
      toast.success("PDF terdownload — lampirkan file itu di jendela WhatsApp yang baru terbuka");
    } catch (e) { toast.error("Gagal membuat PDF dokumen"); }
    finally { setSendingDoc(null); }
  };
  const sendDocEmail = async (d) => {
    const email = (lead?.email || d.client?.email || "").trim();
    if (!email) return toast.error("Email client belum diisi — isi dulu di Edit Lead");
    setSendingDoc(d.doc_id);
    try {
      await api.post(`/documents/${d.doc_id}/send-email`);
      toast.success(`Email terkirim ke ${email} dengan lampiran PDF`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSendingDoc(null); }
  };
  const waPhone = lead?.no_hp ? lead.no_hp.replace(/[^0-9]/g, "").replace(/^0/, "62") : null;
  const waSend = (msg) => {
    if (!waPhone) return;
    const text = (msg || `Halo ${lead.nama}, terima kasih atas minatnya pada layanan kami.`).replace(/\{nama\}/g, lead.nama);
    window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <>
      <Sheet open={!!leadId} onOpenChange={(o) => !o && onClose()}>
        <SheetContent side="right" className="bg-[#0f0f12] border-white/10 w-full sm:max-w-md overflow-y-auto hide-scrollbar p-0">
          {isError ? (
            <div className="flex flex-col items-center justify-center py-20 px-6 text-center gap-2">
              <SheetTitle className="sr-only">Lead tidak ditemukan</SheetTitle>
              <SheetDescription className="sr-only">Lead ini sudah tidak ada atau sudah dihapus</SheetDescription>
              <p className="text-sm text-zinc-400">Lead ini sudah tidak ditemukan (mungkin sudah dihapus).</p>
              <button onClick={onClose} className="text-sm text-blue-400 hover:underline mt-1">Tutup</button>
            </div>
          ) : isLoading || !lead ? (
            <div>
              <SheetTitle className="sr-only">Memuat lead</SheetTitle>
              <SheetDescription className="sr-only">Sedang memuat detail lead</SheetDescription>
            </div>
          ) : (
            <div>
              <SheetHeader className={`p-5 border-l-2 ${AGING[lead.aging?.level || "none"].border} border-b border-white/5`}>
                <div className="flex items-start justify-between gap-2">
                  <SheetTitle className="font-heading text-xl min-w-0">{lead.nama}</SheetTitle>
                  <div className="flex items-center gap-1.5 mr-12 flex-wrap justify-end shrink-0">
                    {lead.ai_score && <span data-testid="lead-score-badge" title={`Skor prioritas AI: ${lead.ai_score.tier}`} className={`text-xs px-2 py-0.5 rounded border font-medium inline-flex items-center gap-1 ${AI_SCORE_CLS}`}><Sparkles size={11} />Skor {lead.ai_score.score}</span>}
                    <span className={`text-xs px-2 py-0.5 rounded border ${PRIORITY[lead.prioritas]}`}>{lead.prioritas}</span>
                  </div>
                </div>
                <SheetDescription className="sr-only">Detail, aktivitas, dan pembayaran lead</SheetDescription>
                <div className="flex items-center gap-3 text-sm text-zinc-400 mt-1 flex-wrap">
                  <span className="flex items-center gap-1"><Phone size={13} />{lead.no_hp || "-"}</span>
                  {lead.email && <span className="flex items-center gap-1"><Mail size={13} />{lead.email}</span>}
                  <span className="flex items-center gap-1"><MapPin size={13} />{lead.kota || "-"}</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">Masuk {fmtDate(lead.created_at)}{lead.status === "Tidak Jelas" ? ` · Expired ${fmtDate(lead.expired_at)}` : ""}</div>
                {lead.status === "Tidak Jelas" && <div className="mt-1 text-[11px] text-zinc-400 bg-zinc-500/15 rounded px-2 py-1 inline-flex items-center gap-1">Data tidak jelas / sampah — bisa di-restore</div>}
                <div className="flex gap-2 mt-3">
                  {waPhone && (
                    <div className="flex-1 flex gap-2">
                      <Button data-testid="wa-btn" onClick={() => waSend()} className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700"><MessageCircle size={15} className="mr-1.5" />WhatsApp</Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button data-testid="wa-template-btn" variant="outline" className="h-9 bg-[#18181B] border-white/10 px-2.5"><MessageSquareText size={15} /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#18181B] border-white/10 w-64">
                          <DropdownMenuLabel>Template Pesan (ketik kode)</DropdownMenuLabel>
                          {templates.map((t) => (
                            <DropdownMenuItem key={t.tpl_id} data-testid={`wa-tpl-${t.code}`} onClick={() => waSend(t.message)} className="flex-col items-start gap-0 focus:bg-white/5">
                              <span className="text-xs text-blue-300">{t.code}</span>
                              <span className="text-xs text-zinc-500 truncate w-full">{t.title}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                  {!readOnly && <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-lead-btn" className="h-9 bg-[#18181B] border-white/10"><Pencil size={14} /></Button>}
                  {!readOnly && (!lead.locked || canUnlock) && <Button variant="outline" onClick={toggleLock} data-testid="lock-btn" className={`h-9 bg-[#18181B] border-white/10 ${lead.locked ? "text-amber-400" : "text-zinc-400"}`}>{lead.locked ? <Lock size={14} /> : <Unlock size={14} />}</Button>}
                  {!readOnly && <Button variant="outline" onClick={del} data-testid="delete-lead-btn" className="h-9 bg-[#18181B] border-white/10 text-red-400"><Trash2 size={14} /></Button>}
                  {!readOnly && lead.status === "Tidak Jelas" && <Button onClick={restore} data-testid="restore-lead-btn" className="h-9 bg-emerald-600 hover:bg-emerald-700"><RotateCcw size={14} className="mr-1.5" />Restore</Button>}
                </div>
                {lead.locked && <div className="mt-2 text-[11px] text-amber-400 flex items-center gap-1"><Lock size={11} />Data nilai & pembayaran terkunci</div>}
              </SheetHeader>

              {/* key={leadId} forces a remount (so defaultValue re-applies) whenever a
                  different lead is opened — otherwise a notification deep-link to e.g. the
                  Diskusi tab of a second lead would be ignored, since Radix Tabs only reads
                  defaultValue once, on the first mount of this same long-lived instance. */}
              <Tabs key={leadId} defaultValue={initialTab || "info"} className="p-5">
                <TabsList className="bg-[#18181B] w-full overflow-x-auto hide-scrollbar justify-start">
                  <TabsTrigger value="info" className="shrink-0 px-3 text-xs" data-testid="tab-info">Info</TabsTrigger>
                  <TabsTrigger value="activity" className="shrink-0 px-3 text-xs" data-testid="tab-activity">Aktivitas</TabsTrigger>
                  <TabsTrigger value="payment" className="shrink-0 px-3 text-xs" data-testid="tab-payment">Bayar</TabsTrigger>
                  <TabsTrigger value="dokumen" className="shrink-0 px-3 text-xs" data-testid="tab-dokumen">Dokumen</TabsTrigger>
                  <TabsTrigger value="kalkulator" className="shrink-0 px-3 text-xs" data-testid="tab-kalkulator">Kalkulator</TabsTrigger>
                  <TabsTrigger value="ai" className="shrink-0 px-3 text-xs" data-testid="tab-ai">AI</TabsTrigger>
                  <TabsTrigger value="diskusi" className="shrink-0 px-3 text-xs" data-testid="tab-diskusi">Diskusi</TabsTrigger>
                  <TabsTrigger value="riwayat" className="shrink-0 px-3 text-xs" data-testid="tab-riwayat">Riwayat</TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="space-y-2.5 mt-4 text-sm">
                  <div className="rounded-xl bg-amber-500/10 border border-amber-500/25 p-3 mb-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] uppercase tracking-wide text-amber-400 font-semibold">🎯 Estimasi Anggaran</span>
                      {!readOnly && !estEditing && (
                        <button data-testid="edit-estimasi" onClick={() => { setEstNilai(String(lead.estimasi_anggaran || 0)); setEstNote(lead.estimasi_note || ""); setEstEditing(true); }}
                          className="text-xs text-blue-400 hover:underline">{lead.estimasi_anggaran > 0 ? "Ubah" : "+ Isi Estimasi"}</button>
                      )}
                    </div>
                    {!estEditing ? (
                      lead.estimasi_anggaran > 0 ? (
                        <div>
                          <div className="font-bold text-amber-300 whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: "clamp(0.85rem, 3vw, 1.125rem)", fontVariantNumeric: "tabular-nums" }} title={rupiah(lead.estimasi_anggaran)}>{rupiah(lead.estimasi_anggaran)}</div>
                          {lead.estimasi_note && <div className="text-xs text-zinc-400 mt-0.5">{lead.estimasi_note}</div>}
                        </div>
                      ) : (
                        <p className="text-xs text-zinc-500">Belum diisi. Catat estimasi anggaran client (mis. setelah konsultasi/visit) — bukan omset, hanya acuan sampai closing.</p>
                      )
                    ) : (
                      <div className="space-y-2">
                        <NumberInput data-testid="estimasi-nilai" placeholder="Estimasi Anggaran (Rp)" value={estNilai} onChange={(e) => setEstNilai(e.target.value)} className="bg-[#18181B] border-white/10 h-9" />
                        <Input data-testid="estimasi-note" placeholder="Catatan (mis. setelah visit ke rumah)" value={estNote} onChange={(e) => setEstNote(e.target.value)} className="bg-[#18181B] border-white/10 h-9" />
                        <div className="flex gap-2">
                          <Button data-testid="save-estimasi" onClick={saveEstimasi} className="flex-1 h-9 bg-blue-600 hover:bg-blue-700">Simpan</Button>
                          <Button onClick={() => setEstEditing(false)} variant="outline" className="h-9 bg-[#18181B] border-white/10">Batal</Button>
                        </div>
                      </div>
                    )}
                  </div>
                  {user?.role !== "sales" && (
                    <div className="flex justify-between items-center gap-4 py-1.5 border-b border-white/5">
                      <span className="text-zinc-500 shrink-0">Sales</span>
                      <AssignSalesDropdown lead={lead} className="w-44" />
                    </div>
                  )}
                  {/* whitespace-nowrap here used to be blanket-applied to every value,
                      including free-text ones (Detail) that can run to a full sentence —
                      nowrap on a long string doesn't truncate it, it just refuses to wrap,
                      forcing the row (and with it the whole sheet) wider than the screen and
                      making the entire page scroll sideways on mobile. Only short/fixed-shape
                      values (dates, currency, single words) get nowrap now; free text wraps
                      normally within its own column via min-w-0 + break-words. */}
                  {[["Status", lead.status, true], ["Layanan", lead.layanan, false], ["Kategori", lead.kategori_customer, false], ["Sumber", lead.sumber_info, false], ["Gender", lead.gender, true], ["Umur", lead.kelompok_umur, true], ["Jumlah Order", lead.jumlah_order, true], ["Nilai Deal", rupiah(lead.nilai_deal), true], ["Detail", lead.detail_order, false], ["Follow-up", fmtDateTime(lead.follow_up_date), true]].map(([l, v, short]) => (
                    <div key={l} className="flex justify-between gap-4 py-1.5 border-b border-white/5">
                      <span className="text-zinc-500 shrink-0">{l}</span>
                      <span className={`text-right font-medium min-w-0 ${short ? "whitespace-nowrap" : "break-words"}`}>{v || "-"}</span>
                    </div>
                  ))}
                  {lead.status === "Lost" && <div className="flex justify-between py-1.5"><span className="text-zinc-500">Alasan Lost</span><span className="text-red-400">{lead.alasan_lost}</span></div>}
                </TabsContent>

                <TabsContent value="activity" className="mt-4">
                  <ScheduledActivityBoard lead={lead} leadId={leadId} refresh={refresh} />
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">Catatan Aktivitas</div>
                  {!readOnly && (
                    <div className="space-y-2 mb-4">
                      <Textarea data-testid="activity-note" placeholder="Catat follow-up / aktivitas…" value={note} onChange={(e) => setNote(e.target.value)} className="bg-[#18181B] border-white/10" rows={2} />
                      <DateTimePicker testId="activity-followup" value={fu} onChange={setFu} placeholder="Follow-up berikutnya (opsional)" className="h-9" />
                      <Button data-testid="save-activity" onClick={addActivity} className="w-full h-9 bg-blue-600 hover:bg-blue-700">Simpan Aktivitas</Button>
                    </div>
                  )}
                  <div className="space-y-2">
                    {(lead.activities || []).slice().reverse().map((a, i) => (
                      <div key={i} className="p-3 rounded-xl bg-[#18181B] text-sm">
                        <p>{a.note}</p>
                        <p className="text-[11px] text-zinc-600 mt-1">{a.by} · {fmtDateTime(a.at)}</p>
                      </div>
                    ))}
                    {(lead.activities || []).length === 0 && <p className="text-sm text-zinc-600 text-center py-4">Belum ada aktivitas</p>}
                  </div>
                </TabsContent>

                <TabsContent value="payment" className="mt-4 space-y-3 text-sm">
                  {!readOnly && lead.locked && canUnlock && (
                    <div className="pb-1 text-center">
                      <Button data-testid="unlock-payment" onClick={toggleLock} variant="outline" className="h-9 bg-[#18181B] border-white/10 text-amber-400"><Unlock size={14} className="mr-1.5" />Buka Kunci Pembayaran</Button>
                    </div>
                  )}
                  {!readOnly && lead.locked && !canUnlock && (
                    <div className="pb-1 text-center text-xs text-zinc-500 flex items-center justify-center gap-1.5"><Lock size={12} />Data terkunci — hanya admin/owner yang bisa membuka</div>
                  )}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-zinc-500"><FilePlus size={13} />Design / Perencanaan</div>
                      {!readOnly && !designEditing && !lead.locked && (
                        <button data-testid="edit-design" onClick={() => { setDesignNilai(String(lead.nilai_deal || 0)); setDesignEditing(true); }}
                          className="text-xs text-blue-400 hover:underline">{lead.nilai_deal > 0 ? "Ubah" : "+ Tambah Design"}</button>
                      )}
                    </div>
                    {!designEditing && (
                      lead.nilai_deal > 0 ? (
                        <div className="p-2.5 rounded-lg bg-[#18181B] flex justify-between items-center">
                          <span className="text-sm text-zinc-400">Nilai Design</span>
                          <span className="text-sm font-semibold text-blue-400">{rupiah(lead.nilai_deal)}</span>
                        </div>
                      ) : (
                        <p className="text-xs text-zinc-600 italic">{lead.locked ? "Belum ada Design — buka kunci dulu untuk menambahkan." : "Belum ada Design. Kalau client langsung produksi (sudah desain di tempat lain), boleh dibiarkan."}</p>
                      )
                    )}
                    {designEditing && (
                      <div className="space-y-2">
                        <NumberInput data-testid="design-nilai" placeholder="Nilai Design (Rp)" value={designNilai} onChange={(e) => setDesignNilai(e.target.value)} className="bg-[#18181B] border-white/10 h-9" />
                        <div className="flex gap-2">
                          <Button data-testid="save-design" onClick={saveDesign} className="flex-1 h-9 bg-blue-600 hover:bg-blue-700">Simpan</Button>
                          <Button onClick={() => setDesignEditing(false)} variant="outline" className="h-9 bg-[#18181B] border-white/10">Batal</Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-white/5 pt-3 mt-3">
                    <PaymentTermins lead={lead} readOnly={readOnly} />
                  </div>

                  <div className="border-t border-white/5 pt-3 mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-zinc-500"><FilePlus size={13} />Produksi</div>
                      {!readOnly && !prodEditing && (
                        <button data-testid="edit-produksi" onClick={() => {
                          const start = lead.nilai_produksi > 0 ? lead.nilai_produksi : (lead.estimasi_anggaran || 0);
                          setProdNilai(String(start)); setProdNote(lead.produksi_note || ""); setProdEditing(true);
                        }} className="text-xs text-blue-400 hover:underline">{lead.nilai_produksi > 0 ? "Ubah" : "+ Tambah Produksi"}</button>
                      )}
                    </div>
                    {!prodEditing && (
                      lead.nilai_produksi > 0 ? (
                        <div className="p-2.5 rounded-lg bg-[#18181B] flex justify-between items-start">
                          <div>
                            {lead.produksi_note && <div className="text-sm">{lead.produksi_note}</div>}
                            <div className="text-[10px] text-zinc-600">Sejak {fmtDateTime(lead.produksi_closing_date)}</div>
                          </div>
                          <span className="text-sm font-semibold text-emerald-400">{rupiah(lead.nilai_produksi)}</span>
                        </div>
                      ) : (
                        <p className="text-xs text-zinc-600 italic">Belum lanjut ke Produksi. Nilai Design/Perencanaan di atas tidak akan berubah kalau ditambahkan.</p>
                      )
                    )}
                    {prodEditing && (
                      <div className="space-y-2">
                        {lead.nilai_produksi === 0 && lead.estimasi_anggaran > 0 && (
                          <p className="text-[11px] text-amber-400">Diisi otomatis dari Estimasi Anggaran ({rupiah(lead.estimasi_anggaran)}) — boleh diubah sebelum simpan.</p>
                        )}
                        <NumberInput data-testid="produksi-nilai" placeholder="Nilai Produksi (Rp)" value={prodNilai} onChange={(e) => setProdNilai(e.target.value)} className="bg-[#18181B] border-white/10 h-9" />
                        <Input data-testid="produksi-note" placeholder="Catatan (opsional, mis. termasuk diskon design)" value={prodNote} onChange={(e) => setProdNote(e.target.value)} className="bg-[#18181B] border-white/10 h-9" />
                        <div className="flex gap-2">
                          <Button data-testid="save-produksi" onClick={saveProduksi} className="flex-1 h-9 bg-blue-600 hover:bg-blue-700">Simpan</Button>
                          <Button onClick={() => setProdEditing(false)} variant="outline" className="h-9 bg-[#18181B] border-white/10">Batal</Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {lead.nilai_produksi > 0 && (
                    <div className="border-t border-white/5 pt-3 mt-3">
                      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-zinc-500 mb-2"><Wallet size={13} />Pembayaran Produksi</div>
                      <PaymentTermins lead={lead} readOnly={readOnly} kind="produksi" />
                    </div>
                  )}

                  <div className="border-t border-white/5 pt-3 mt-3">
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-zinc-500 mb-2"><FilePlus size={13} />Adendum Proyek</div>
                    {(lead.addendums || []).length > 0 && (
                      <div className="space-y-1.5 mb-3">
                        {lead.addendums.map((a, i) => (
                          <div key={i} className="flex justify-between items-start p-2.5 rounded-lg bg-[#18181B]">
                            <div><div className="text-sm">{a.note}</div><div className="text-[10px] text-zinc-600">{a.by} · {fmtDateTime(a.at)}</div></div>
                            <span className={`text-sm font-semibold ${a.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>{a.amount >= 0 ? "+" : ""}{rupiah(a.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {!readOnly && (
                      <div className="space-y-2">
                        <Input data-testid="addendum-note" placeholder="Keterangan adendum (mis. tambah wardrobe)" value={addNote} onChange={(e) => setAddNote(e.target.value)} className="bg-[#18181B] border-white/10 h-9" />
                        <NumberInput data-testid="addendum-amount" placeholder="Perubahan nilai (+/-)" value={addAmount} onChange={(e) => setAddAmount(e.target.value)} className="bg-[#18181B] border-white/10 h-9" />
                        <Button data-testid="save-addendum" onClick={addAddendum} variant="outline" className="w-full h-9 bg-[#18181B] border-white/10"><FilePlus size={14} className="mr-1.5" />Tambah Adendum</Button>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="dokumen" className="mt-4">
                  {!readOnly && (
                    <div className="flex gap-2 mb-3">
                      <Button data-testid="new-quotation" onClick={() => { setEditingDoc(null); setDocOpen(true); }} className="flex-1 h-9 bg-blue-600 hover:bg-blue-700"><FileText size={14} className="mr-1.5" />Buat Dokumen</Button>
                    </div>
                  )}
                  <div className="space-y-2">
                    {regularDocs.map((d) => (
                      <div key={d.doc_id} className="p-3 rounded-xl bg-[#18181B] flex items-center justify-between gap-2" data-testid={`doc-row-${d.doc_id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate flex items-center gap-1.5">
                            {docTypeLabel(d)} · {d.number}
                            {d.revenue_kind === "produksi" && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30 shrink-0">Produksi</span>}
                          </div>
                          <div className="text-[11px] text-zinc-500">{fmtDate(d.date)} · <span className="text-emerald-400">{rupiah(d.total)}</span> · {d.payment?.status}</div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => sendDocWa(d)} disabled={sendingDoc === d.doc_id} title="Kirim ke Client via WhatsApp (download PDF + buka WA)" data-testid={`doc-wa-${d.doc_id}`} className="text-emerald-400 p-1.5 hover:bg-white/5 rounded-lg disabled:opacity-40">{sendingDoc === d.doc_id ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />}</button>
                          <button onClick={() => sendDocEmail(d)} disabled={sendingDoc === d.doc_id} title="Kirim ke Client via Email (PDF terlampir otomatis)" data-testid={`doc-email-${d.doc_id}`} className="text-amber-300 p-1.5 hover:bg-white/5 rounded-lg disabled:opacity-40">{sendingDoc === d.doc_id ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}</button>
                          <a href={`/cetak/${d.doc_id}`} target="_blank" rel="noreferrer" data-testid={`doc-print-${d.doc_id}`} className="text-cyan-300 p-1.5 hover:bg-white/5 rounded-lg"><Printer size={15} /></a>
                          {!readOnly && <button onClick={() => duplicateDoc(d)} title="Duplikat dokumen ini" data-testid={`doc-dup-${d.doc_id}`} className="text-zinc-400 p-1.5 hover:bg-white/5 rounded-lg"><Copy size={14} /></button>}
                          {!readOnly && <button onClick={() => { setEditingDoc(d); setDocOpen(true); }} className="text-blue-300 p-1.5 hover:bg-white/5 rounded-lg"><Pencil size={14} /></button>}
                          {!readOnly && <button onClick={() => delDoc(d.doc_id)} className="text-red-400 p-1.5 hover:bg-red-500/10 rounded-lg"><Trash2 size={14} /></button>}
                        </div>
                      </div>
                    ))}
                    {regularDocs.length === 0 && <p className="text-sm text-zinc-600 text-center py-6">Belum ada dokumen. Buat Quotation atau Invoice.</p>}
                  </div>
                </TabsContent>

                <TabsContent value="kalkulator" className="mt-4">
                  {!readOnly && (
                    <div className="flex gap-2 mb-3">
                      <Button data-testid="new-kalkulator" onClick={() => { setEditingKalk(null); setKalkOpen(true); }} className="flex-1 h-9 bg-blue-600 hover:bg-blue-700"><Calculator size={14} className="mr-1.5" />Buat Kalkulator Estimasi</Button>
                    </div>
                  )}
                  <div className="space-y-2">
                    {kalkDocs.map((d) => {
                      const isOpsi2 = d.kalkulator_data?.totals?.opsi === 2;
                      return (
                      <div key={d.doc_id} className="p-3 rounded-xl bg-[#18181B] flex items-center justify-between gap-2" data-testid={`doc-row-${d.doc_id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate flex items-center gap-1.5">
                            {docTypeLabel(d)} · {d.number}
                            {isOpsi2 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300 font-semibold shrink-0">Opsi 2 · Semua Kelas</span>}
                          </div>
                          <div className="text-[11px] text-zinc-500">{fmtDate(d.date)} · <span className="text-emerald-400">{isOpsi2 ? "lihat rincian paket" : rupiah(d.total)}</span></div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => sendDocWa(d)} disabled={sendingDoc === d.doc_id} title="Kirim ke Client via WhatsApp (download PDF + buka WA)" data-testid={`doc-wa-${d.doc_id}`} className="text-emerald-400 p-1.5 hover:bg-white/5 rounded-lg disabled:opacity-40">{sendingDoc === d.doc_id ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />}</button>
                          <button onClick={() => sendDocEmail(d)} disabled={sendingDoc === d.doc_id} title="Kirim ke Client via Email (PDF terlampir otomatis)" data-testid={`doc-email-${d.doc_id}`} className="text-amber-300 p-1.5 hover:bg-white/5 rounded-lg disabled:opacity-40">{sendingDoc === d.doc_id ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}</button>
                          <a href={`/cetak/${d.doc_id}`} target="_blank" rel="noreferrer" data-testid={`doc-print-${d.doc_id}`} className="text-cyan-300 p-1.5 hover:bg-white/5 rounded-lg"><Printer size={15} /></a>
                          {!readOnly && <button onClick={() => duplicateDoc(d)} title="Duplikat dokumen ini" data-testid={`doc-dup-${d.doc_id}`} className="text-zinc-400 p-1.5 hover:bg-white/5 rounded-lg"><Copy size={14} /></button>}
                          {!readOnly && <button onClick={() => { setEditingKalk(d); setKalkOpen(true); }} className="text-blue-300 p-1.5 hover:bg-white/5 rounded-lg"><Pencil size={14} /></button>}
                          {!readOnly && <button onClick={() => delDoc(d.doc_id)} className="text-red-400 p-1.5 hover:bg-red-500/10 rounded-lg"><Trash2 size={14} /></button>}
                        </div>
                      </div>
                      );
                    })}
                    {kalkDocs.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-white/10 py-10 px-5 text-center">
                        <Calculator size={28} className="mx-auto mb-3 text-zinc-600" />
                        <p className="text-sm text-zinc-500">Belum ada kalkulator estimasi untuk lead ini.</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="ai" className="mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={15} className="text-blue-400" />
                    <span className="text-sm font-medium">AI Sales Copilot</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded border bg-zinc-500/15 text-zinc-400 border-zinc-500/30">Coming Soon</span>
                  </div>
                  {!AI_COPILOT_ENABLED ? (
                    <div data-testid="copilot-coming-soon" className="rounded-xl border border-white/10 bg-[#18181B] p-5 text-center">
                      <Sparkles size={22} className="text-blue-400 mx-auto mb-2" />
                      <p className="text-sm font-medium mb-1">Fitur ini akan segera hadir</p>
                      <p className="text-xs text-zinc-500">AI Sales Copilot sedang dipersiapkan ulang dan belum aktif untuk saat ini.</p>
                    </div>
                  ) : (
                    <>
                      {lead.ai_score?.factors?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {lead.ai_score.factors.map((f, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-400">{f}</span>)}
                        </div>
                      )}
                      <p className="text-[11px] text-zinc-500 mb-3">Rekomendasi langkah follow-up berikutnya + draft pesan WhatsApp, dibuat AI berdasar kondisi lead ini.</p>
                      <Button data-testid="copilot-btn" onClick={getCopilot} disabled={copLoading} className="w-full h-9 bg-blue-600 hover:bg-blue-700">
                        {copLoading ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Sparkles size={15} className="mr-1.5" />}{copilot ? "Regenerasi Saran AI" : "Dapatkan Saran AI"}
                      </Button>
                      {copErr && <div data-testid="copilot-error" className="mt-3 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{copErr}</div>}
                      {copilot && (
                        <div className="mt-4 space-y-3" data-testid="copilot-result">
                          {copilot.insight && <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-sm text-blue-700 dark:text-blue-200">{copilot.insight}</div>}
                          {(copilot.next_steps || []).length > 0 && (
                            <div className="p-3 rounded-xl bg-[#18181B]">
                              <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Langkah Berikutnya</div>
                              <ol className="space-y-1.5 text-sm list-decimal list-inside text-zinc-300">
                                {copilot.next_steps.map((s, i) => <li key={i}>{s}</li>)}
                              </ol>
                            </div>
                          )}
                          {copilot.wa_draft && (
                            <div className="p-3 rounded-xl bg-[#18181B]">
                              <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Draft WhatsApp</div>
                              <p className="text-sm whitespace-pre-wrap text-zinc-300">{copilot.wa_draft}</p>
                              {waPhone && <Button data-testid="copilot-wa-send" onClick={() => waSend(copilot.wa_draft)} className="w-full h-9 mt-2 bg-emerald-600 hover:bg-emerald-700"><MessageCircle size={14} className="mr-1.5" />Kirim via WhatsApp</Button>}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>

                <TabsContent value="diskusi" className="mt-4">
                  <p className="text-[11px] text-zinc-500 mb-2">Diskusi internal tim khusus client ini (tidak terlihat client).</p>
                  <div className="card-surface rounded-xl p-2"><ChatBox channel={leadId} height="46vh" /></div>
                </TabsContent>


                <TabsContent value="riwayat" className="mt-4">
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-zinc-500 mb-2"><History size={13} />Audit Log — Riwayat Perubahan</div>
                  <div className="space-y-2">
                    {audit.map((a, i) => (
                      <div key={i} className="p-3 rounded-xl bg-[#18181B] text-sm">
                        <div className="flex justify-between"><span className="font-medium text-blue-300">{a.action}</span><span className="text-[10px] text-zinc-600">{fmtDateTime(a.at)}</span></div>
                        {a.detail && <p className="text-xs text-zinc-400 mt-0.5">{a.detail}</p>}
                        <p className="text-[10px] text-zinc-600 mt-0.5">oleh {a.by}</p>
                      </div>
                    ))}
                    {audit.length === 0 && <p className="text-sm text-zinc-600 text-center py-4">Belum ada perubahan</p>}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>
      <LeadFormDialog open={editOpen} onOpenChange={setEditOpen} lead={lead} />
      {lead && <DocumentDialog open={docOpen} onOpenChange={setDocOpen} lead={lead} doc={editingDoc} />}
      {lead && <KalkulatorBuilder open={kalkOpen} onOpenChange={setKalkOpen} lead={lead} doc={editingKalk} />}
    </>
  );
}
